import { type QueryObserverResult } from '@tanstack/react-query'
import { LoaderCircleIcon } from 'lucide-react'
import { createContext, useContext, useMemo } from 'react'
import { Outlet } from 'react-router'

import { Banner } from 'slab'
import {
  useGetPayrollManageConfig as useGetPayrollConfig
} from 'utils/web_api'
import { type PayrollPayrollConfig as PayrollConfig } from 'utils/web_api.schemas'

const PayrollConfigContext = createContext<{
  payrollConfig: PayrollConfig | null;
  refetch: ReturnType<typeof useGetPayrollConfig>['refetch'];
}>({
  payrollConfig: null,
  refetch: async () =>
    ({
      data: null,
      isLoading: false,
      isError: false,
      error: null,
    } as QueryObserverResult<unknown, unknown>),
})

export function usePayrollConfig () {
  return useContext(PayrollConfigContext)
}

export function PayrollConfigProvider () {
  const { data, isLoading, error, refetch } = useGetPayrollConfig()

  const value = useMemo(
    () => ({ payrollConfig: data || null, refetch }),
    [data, refetch]
  )

  if (isLoading) {
    return (
      <div className='flex grow items-center justify-center'>
        <LoaderCircleIcon size={24} className='animate-spin text-gray-500' />
      </div>
    )
  }

  if (error) {
    return (
      <div className='mt-20 w-full max-w-[45rem] mx-auto'>
        <Banner type='critical' title='Error loading payroll config'>
          {error instanceof Error && error.message}
        </Banner>
      </div>
    )
  }

  return (
    <PayrollConfigContext.Provider value={value}>
      <Outlet />
    </PayrollConfigContext.Provider>
  )
}
